import React, { ChangeEvent, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { CheckboxBlockField } from '@app/form';
import {
  Devices,
  ParticipantFieldCategory,
  useCurrentParticipant,
  useParticipantFieldsCategory,
} from '@app/api';
import { useTranslation } from 'react-i18next';
import { ParticipantInputByName } from '../../Profile/components/ParticipantInputByName';
import { InformationSection } from '../../Profile/components/InformationSection';
import { useField } from 'formik';

const useStyles = createUseStyles((theme: Theme) => ({
  containerLoader: {
    width: '100%',
    background: theme.colors.white,
    minHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  descriptionContainer: {
    background: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    marginBottom: theme.marginBase * 5,
  },
  description: {
    ...theme.fonts.caption,
  },
  checkBoxContainer: {
    marginBottom: theme.marginBase * 2,
  },
  titleQuestion: {
    ...theme.fonts.label,
    marginBottom: theme.marginBase / 2,
  },
  descriptionQuestion: {
    ...theme.fonts.caption,
    marginBottom: theme.marginBase / 2,
    color: theme.colors.inkSecondary,
  },
  boxContainer: {
    marginTop: theme.marginBase * 3,
  },
}));

interface Props {
  noTitle?: boolean;
}

const computerDevices = [
  Devices.WINDOWS_COMPUTER,
  Devices.MAC_OS_COMPUTER,
  Devices.LINUX_COMPUTER,
];
const tabletDevices = [Devices.IOS_TABLET, Devices.ANDROID_TABLET];
const phoneDevices = [Devices.IOS_SMART_PHONE, Devices.ANDROID_SMART_PHONE];
const watchDevices = [Devices.IOS_SMART_WATCH, Devices.ANDROID_SMART_WATCH];
const EMPTY_ARRAY: Devices[] = [];

export const DevicesFields = ({ noTitle }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { data: participant } = useCurrentParticipant(true);
  const { t } = useTranslation();

  const categoryConfig = useParticipantFieldsCategory(
    ParticipantFieldCategory.DIGITAL_HABITS,
  );
  const [computerState, setComputer] = useState<boolean | undefined>(undefined);
  const computer =
    computerState !== undefined
      ? computerState
      : computerDevices.some((device) =>
          participant?.devices?.includes(device),
        );

  const [tabletState, setTablet] = useState<boolean | undefined>(undefined);
  const tablet =
    tabletState !== undefined
      ? tabletState
      : tabletDevices.some((device) => participant?.devices?.includes(device));

  const [phoneState, setPhone] = useState<boolean | undefined>(undefined);
  const phone =
    phoneState !== undefined
      ? phoneState
      : phoneDevices.some((device) => participant?.devices?.includes(device));

  const [watchState, setWatch] = useState<boolean | undefined>(undefined);
  const watch =
    watchState !== undefined
      ? watchState
      : watchDevices.some((device) => participant?.devices?.includes(device));

  const [{ value }, _, { setValue }] = useField('devices');
  const devices = value || EMPTY_ARRAY;

  const onChangeDevice = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.currentTarget.value;
    const index = devices.indexOf(selected);
    if (index > -1) {
      devices.splice(index, 1);
      setValue([...devices]);
      return;
    } else {
      setValue([...devices, selected]);
    }
  };

  const removeDevices = (toRemove: Devices[]) => {
    if (!devices) return;
    setValue(devices.filter((device: Devices) => !toRemove.includes(device)));
  };

  if (!categoryConfig) {
    return null;
  }

  return (
    <InformationSection
      title={noTitle ? undefined : categoryConfig.title}
      icon="ri-computer-line"
      iconColor="blue"
      description={categoryConfig.description}
    >
      <ParticipantInputByName fieldName="techSavviness" />
      <div className={classes.boxContainer}>
        <h1 className={classes.titleQuestion}>
          {t('devicesSelection.devices.title')}
        </h1>
        <CheckboxBlockField
          label={t('devicesSelection.devices.computer')}
          type="checkbox"
          icon="ri-computer-line"
          variant="green"
          value="Computer"
          className={classes.checkBoxContainer}
          onChange={() => {
            setComputer(!computer);
            removeDevices(computerDevices);
          }}
          checked={computer}
        />
        <CheckboxBlockField
          label={t('devicesSelection.devices.tablet')}
          type="checkbox"
          icon="ri-tablet-line"
          variant="green"
          name="Tablet"
          value="Tablet"
          className={classes.checkBoxContainer}
          onChange={() => {
            setTablet(!tablet);
            removeDevices(tabletDevices);
          }}
          checked={tablet}
        />
        <CheckboxBlockField
          label={t('devicesSelection.devices.smartphone')}
          type="checkbox"
          icon="ri-smartphone-line"
          variant="green"
          name="Phone"
          value="Phone"
          className={classes.checkBoxContainer}
          onChange={() => {
            setPhone(!phone);
            removeDevices(phoneDevices);
          }}
          checked={phone}
        />
        <CheckboxBlockField
          label={t('devicesSelection.devices.watch')}
          type="checkbox"
          variant="green"
          name="Watch"
          value="Watch"
          className={classes.checkBoxContainer}
          onChange={() => {
            setWatch(!watch);
            removeDevices(watchDevices);
          }}
          checked={watch}
        />
      </div>
      {computer && (
        <div className={classes.boxContainer}>
          <h1 className={classes.titleQuestion}>
            {t('devicesSelection.computerOS.title')}
          </h1>
          <p className={classes.descriptionQuestion}>
            {t('devicesSelection.computerOS.description')}
          </p>
          <CheckboxBlockField
            label={t('devicesSelection.computerOS.windows')}
            type="checkbox"
            variant="green"
            name="windows"
            value={Devices.WINDOWS_COMPUTER}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.WINDOWS_COMPUTER)}
            className={classes.checkBoxContainer}
          />
          <CheckboxBlockField
            label={t('devicesSelection.computerOS.macos')}
            type="checkbox"
            variant="green"
            name="macOs"
            value={Devices.MAC_OS_COMPUTER}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.MAC_OS_COMPUTER)}
            className={classes.checkBoxContainer}
          />
          <CheckboxBlockField
            label={t('devicesSelection.computerOS.linux')}
            type="checkbox"
            variant="green"
            name="linux"
            value={Devices.LINUX_COMPUTER}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.LINUX_COMPUTER)}
            className={classes.checkBoxContainer}
          />
        </div>
      )}
      {tablet && (
        <div className={classes.boxContainer}>
          <h1 className={classes.titleQuestion}>
            {t('devicesSelection.tabletOS.title')}
          </h1>
          <p className={classes.descriptionQuestion}>
            {t('devicesSelection.tabletOS.description')}
          </p>
          <CheckboxBlockField
            label={t('devicesSelection.tabletOS.ios')}
            type="checkbox"
            variant="green"
            name="tabletIOS"
            value={Devices.IOS_TABLET}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.IOS_TABLET)}
            className={classes.checkBoxContainer}
          />
          <CheckboxBlockField
            label={t('devicesSelection.tabletOS.android')}
            type="checkbox"
            variant="green"
            name="tabletAndroid"
            value={Devices.ANDROID_TABLET}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.ANDROID_TABLET)}
            className={classes.checkBoxContainer}
          />
        </div>
      )}
      {phone && (
        <div className={classes.boxContainer}>
          <h1 className={classes.titleQuestion}>
            {t('devicesSelection.smartphoneOS.title')}
          </h1>
          <p className={classes.descriptionQuestion}>
            {t('devicesSelection.smartphoneOS.description')}
          </p>
          <CheckboxBlockField
            label={t('devicesSelection.tabletOS.ios')}
            type="checkbox"
            variant="green"
            name="phoneIOS"
            value={Devices.IOS_SMART_PHONE}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.IOS_SMART_PHONE)}
            className={classes.checkBoxContainer}
          />
          <CheckboxBlockField
            label={t('devicesSelection.tabletOS.android')}
            type="checkbox"
            variant="green"
            name="phoneAndroid"
            value={Devices.ANDROID_SMART_PHONE}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.ANDROID_SMART_PHONE)}
            className={classes.checkBoxContainer}
          />
        </div>
      )}
      {watch && (
        <div className={classes.boxContainer}>
          <h1 className={classes.titleQuestion}>
            {t('devicesSelection.watchOS.title')}
          </h1>
          <p className={classes.descriptionQuestion}>
            {t('devicesSelection.watchOS.description')}
          </p>
          <CheckboxBlockField
            label={t('devicesSelection.tabletOS.ios')}
            type="checkbox"
            variant="green"
            name="watchIOS"
            value={Devices.IOS_SMART_WATCH}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.IOS_SMART_WATCH)}
            className={classes.checkBoxContainer}
          />
          <CheckboxBlockField
            label={t('devicesSelection.tabletOS.android')}
            type="checkbox"
            variant="green"
            name="watchAndroid"
            value={Devices.ANDROID_SMART_WATCH}
            onChange={onChangeDevice}
            checked={devices.includes(Devices.ANDROID_SMART_WATCH)}
            className={classes.checkBoxContainer}
          />
        </div>
      )}
    </InformationSection>
  );
};
